import { useMantineTheme } from '@mantine/core';
import { useApiGetEntityCounts } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import RichTooltip from '@repo/common/components/RichTooltip/RichTooltip';
import type { SelectablePropertyItem } from '@repo/common/components/SingleSelector/types';
import { EntityType } from '@repo/common/enums/entityType';
import { UserRole } from '@repo/common/enums/UserRole';
import { Icon, Text } from '@repo/foundations';
import { useMount } from 'ahooks';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import type { IQuery, ISecodaEntity } from '../../api';
import {
	useAuthUser,
	useIntegration,
	useIntegrationSupport,
	usePolicyEnabled,
	useSecodaEntity,
	useSuspenseFeatureAccess,
	useUpdateSecodaEntity,
} from '../../api';
import useSuspenseSecodaEntity from '../../api/hooks/secodaEntity/useSuspenseSecodaEntity';
import Documentation from '../../components/Documentation';
import EntityDiscussions from '../../components/EntityDiscussions';
import { getEntityPageSidebarProps } from '../../components/EntityDrawer/utils';
import EntityLogo from '../../components/EntityLogo/EntityLogo';
import EntityPageLayout from '../../components/EntityPageLayout';
import EntityPageLayoutSkeleton from '../../components/EntityPageLayout/EntityPageLayoutSkeleton';
import { StaticProperty } from '../../components/EntityPageLayout/EntityPropertySidebar';
import { useLineageTab } from '../../components/EntityPageLayout/hooks';
import { getDefaultTab } from '../../components/EntityPageLayout/utils';
import EntityPolicyList from '../../components/EntityPolicyList/EntityPolicyList';
import { LineageTab } from '../../components/LineageGraph/LineageTab';
import OverviewPageTabs from '../../components/Overview/OverviewPageTabs';
import { QueryList } from '../../components/QueryListV2/QueryList';
import { useCheckIAMPermission } from '../../utils/authorization/roles';
import { useTabNavigate } from '../../utils/hook/useTabNavigate';
import { useParamsIdSuffixUuid } from '../../utils/hook/utils';
import MonitorsTab from '../TableEntityPage/TableEntityTabs/MonitorsTab';
import Tests from '../TablePage/Tests';
import type { DjangoValueType } from '../TemplatePage/types';

function ReadOnlyPublishField({
	columnParent,
}: {
	columnParent: ISecodaEntity | undefined;
}) {
	const theme = useMantineTheme();
	const isPublished = columnParent?.published || false;

	const statusOption: SelectablePropertyItem = {
		label: isPublished ? 'Published' : 'Draft',
		value: isPublished,
		icon: <Icon name={isPublished ? 'circleCheckFilled' : 'circle'} />,
		iconColor: theme.other.getColor('icon/success/default'),
		fill: 'surface/primary/disabled',
	};

	return (
		<RichTooltip
			title="Read-only"
			body={
				<Text size="sm">
					Column status is automatically set based on the parent table&apos;s
					status.
				</Text>
			}
			position="left"
		>
			<StaticProperty type="badge" label="Status" value={statusOption} />
		</RichTooltip>
	);
}

const InnerColumnEntityPage = observer(({ id }: { id: string }) => {
	const { user, workspace } = useAuthUser();
	const hasPolicyAccess = usePolicyEnabled();
	const navigateToTab = useTabNavigate();

	const { lineageAccess, monitoringAccess, secodaCoreAccess } =
		useSuspenseFeatureAccess();
	const { data: columnEntity } = useSuspenseSecodaEntity({
		id,
	});

	useMount(() => {
		trackEvent('column_entity/open', {}, user, workspace);
	});

	const { mutateAsync } = useUpdateSecodaEntity({});

	const updateColumnEntity = useCallback(
		async (key: string, value: DjangoValueType, saveRemotely = true) => {
			if (saveRemotely) {
				await mutateAsync({
					data: {
						id: columnEntity!.id,
						[key]: value,
					},
				});
				trackEvent('column/properties/update', {}, user, workspace!);
			}
		},
		[columnEntity, mutateAsync, user, workspace]
	);

	const { data: columnParent } = useSecodaEntity({
		id: columnEntity?.parent as string,
		options: {
			enabled: !!columnEntity && !!columnEntity.parent,
		},
	});

	const { data: integration } = useIntegration({
		id: columnParent?.integration || '',
		options: {
			enabled: !!columnParent?.integration,
		},
	});

	const { data: integrationSupport } = useIntegrationSupport();

	const integrationKeys = integrationSupport
		? Object.keys(integrationSupport)
		: [];
	const isMonitoringEnabled =
		integrationKeys.includes(integration?.type ?? '') &&
		Boolean(columnParent?.integration) &&
		!!workspace?.monitoring_enabled;

	const { data: entityCounts, isLoading } = useApiGetEntityCounts(
		{
			queryParams: {
				entity_id: columnEntity?.id ?? '',
			},
		},
		{
			enabled: !!columnEntity?.id,
		}
	);

	const { hasPermission: hasQueriesAccess } = useCheckIAMPermission({
		v1AllowedRoles: [
			UserRole.ADMIN,
			UserRole.EDITOR,
			UserRole.VIEWER,
			UserRole.GUEST,
		],
		v2Permission: 'Queries.Read',
	});

	const { hasPermission: hasQuestionsAccess } = useCheckIAMPermission({
		v1AllowedRoles: [
			UserRole.ADMIN,
			UserRole.EDITOR,
			UserRole.VIEWER,
			UserRole.GUEST,
		],
		v2Permission: 'Questions.Read',
	});

	const { hasPermission: hasTestsAccess } = useCheckIAMPermission({
		v1AllowedRoles: [
			UserRole.ADMIN,
			UserRole.EDITOR,
			UserRole.VIEWER,
			UserRole.GUEST,
		],
		v2Permission: 'Tests.Read',
	});

	const { lineageFlowStore, lineageFlowUIStore, lineageFilterStore } =
		useLineageTab(
			columnEntity.id,
			columnEntity.published,
			EntityType.column,
			columnEntity.native_type
		);

	if (!columnEntity) {
		return <EntityPageLayoutSkeleton />;
	}

	const tabs = useMemo(
		() => [
			{
				value: 'lineage',
				label: 'Lineage',
				condition: !!lineageAccess,
				component: (
					<LineageTab
						lineageFlowStore={lineageFlowStore}
						lineageFlowUIStore={lineageFlowUIStore}
						lineageFilterStore={lineageFilterStore}
					/>
				),
			},
			{
				value: 'monitors',
				label: 'Monitors',
				condition: isMonitoringEnabled && !!monitoringAccess,
				component: (
					<MonitorsTab
						integrationId={columnParent?.integration || ''}
						tableId={columnEntity.id}
					/>
				),
			},
			{
				value: 'queries',
				label: 'Queries',
				condition:
					!isLoading && Boolean(entityCounts?.query_count) && hasQueriesAccess,
				component: (
					<QueryList
						entity={
							columnEntity as unknown as ISecodaEntity & {
								creation_query?: IQuery;
							}
						}
					/>
				),
			},
			{
				value: 'tests',
				label: 'Tests',
				disabled: isLoading,
				condition:
					!isLoading && Boolean(entityCounts?.test_count) && hasTestsAccess,
				component: <Tests entity={columnEntity} />,
			},
			{
				value: 'discussions',
				label: 'Questions',
				condition: hasQuestionsAccess && !!secodaCoreAccess,
				component: <EntityDiscussions entity={columnEntity} />,
			},
			{
				value: 'documentation',
				label: 'Documentation',
				component: <Documentation entity={columnEntity} />,
			},
		],
		[
			columnEntity,
			isLoading,
			entityCounts?.query_count,
			entityCounts?.test_count,
			hasQueriesAccess,
			hasQuestionsAccess,
			hasTestsAccess,
			isMonitoringEnabled,
			columnParent?.integration,
			lineageAccess,
			monitoringAccess,
			secodaCoreAccess,
		]
	);

	if (hasPolicyAccess) {
		tabs.push({
			value: 'policies',
			label: 'Policies',
			component: <EntityPolicyList entity={columnEntity} />,
		});
	}

	const { tab: urlTabParam } = useParams();

	const defaultTab = useMemo(() => getDefaultTab(tabs), [tabs]);
	const currentTab = urlTabParam ?? defaultTab;

	const handleTabChange = useCallback(
		(updatedTab: string) => {
			if (updatedTab === 'lineage') {
				trackEvent('column/lineage/open', {}, user, workspace);
			}
			navigateToTab(updatedTab);
		},
		[navigateToTab, user, workspace]
	);

	return (
		<EntityPageLayout
			key={columnEntity.id}
			name="Column"
			isReadOnlyTitle
			entity={columnEntity}
			icon={<EntityLogo entity={columnEntity} size={32} />}
			updateEntity={updateColumnEntity}
			withDescription
			withEntityByteSize
			withEntityRowCount
			withCustomPropertyEditors
			withCustomPublishElement={
				<ReadOnlyPublishField columnParent={columnParent} />
			}
			{...getEntityPageSidebarProps(EntityType.column)}
		>
			<OverviewPageTabs
				defaultTab={defaultTab}
				key={columnEntity.id}
				currentTab={currentTab}
				tabs={tabs}
				onTabChange={handleTabChange}
			/>
		</EntityPageLayout>
	);
});

// by memoizing the inner component, we can avoid re-rendering the entire page on tab changes and only re-render the tab panel
function ColumnEntityPage() {
	const id = useParamsIdSuffixUuid();

	return <InnerColumnEntityPage id={id} />;
}

export default ColumnEntityPage;
